import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  shouldShowAd,
} from 'lib/article';
import {
  article as ArticlePropType,
  showBlogNavigation as showBlogNavigationPropType,
} from 'lib/CustomPropTypes';
import { listenForHFSBump, disconnectHFSBump } from 'lib/listenForHFSBump';
import BTE from 'lib/BTE';
import { verticalSlugMap } from 'lib/vertical';
import {
  ARTICLE_TAXONOMY_SECTION_NUMBERING,
} from 'lib/brandFeatures';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { extractTermPaths } from 'lib/taxonomy';

import Ad from 'components/Ad';
import { ArticleFoot } from 'components/Article/ArticleFoot';
import { getNormalizedArticleBody } from 'components/Article/articleUtils';
import { monitorArticleView } from 'components/Article/ArticleBody/bodyUtils';
import { getSections } from 'components/Article/getSections';
import { ExpandedBylineContributors } from 'components/Article/ExpandedBylineContributors';
import { BodyBottomRecommended } from 'components/Article/ArticleBody/BodyBottomRecommended';
import { BodyRightRail } from 'components/Article/ArticleBody/BodyRightRail';
import { BodyTaboola } from 'components/Article/ArticleBody/BodyTaboola';

import { ShowBlogByline } from 'components/ShowBlog/ShowBlogByline';
import { ShowBlogNavigation } from 'components/ShowBlog/Navigation';

import { downsizeEmbeds } from './downsizeEmbeds';

import './styles.themed.scss';

const block = 'showblog-body';

const {
  news: NEWS,
  today: TODAY,
} = verticalSlugMap;

export class ShowBlogBody extends React.Component {
  ads = [];

  HFSObserver = null;

  static propTypes = {
    article: ArticlePropType.isRequired,
    isChromeless: PropTypes.bool,
    path: PropTypes.string.isRequired,
    gridBodyClasses: PropTypes.string,
    showBlogNav: showBlogNavigationPropType.isRequired,
    taxonomy: PropTypes.shape({
      primarySection: PropTypes.shape({
        path: PropTypes.string,
      }),
    }),
    vertical: PropTypes.string.isRequired,
  };

  static contextTypes = {
    isShowBlog: PropTypes.bool,
  };

  static childContextTypes = {
    canonicalUrl: PropTypes.string,
    setStickyVideoFlag: PropTypes.func,
  };

  static defaultProps = {
    isChromeless: false,
    taxonomy: undefined,
    gridBodyClasses: '',
  };

  constructor(props) {
    super(props);
    this.state = {
      boxflexRendered: false,
      hasStickyVideo: false,
    };
  }

  getChildContext() {
    const { article } = this.props;
    return {
      setStickyVideoFlag: this.setStickyVideoFlag,
      canonicalUrl: article?.url?.primary,
    };
  }

  componentDidMount() {
    const { article } = this.props;
    BTE.on('articleInView', monitorArticleView(article, this.updateShowTaboola));

    if (this.ads) {
      this.HFSObserver = listenForHFSBump(this, 'isScrollingUp');
    }
  }

  componentWillUnmount() {
    const { article } = this.props;
    BTE.remove('articleInView', monitorArticleView(article, this.updateShowTaboola));

    if (this.ads) {
      disconnectHFSBump(this.HFSObserver);
    }
  }

  setStickyVideoFlag = (hasStickyVideo) => {
    this.setState({ hasStickyVideo });
  }

  updateShowTaboola = (showTaboola) => {
    this.setState({ showTaboola });
  }

  getBodyContainerClasses = () => {
    const {
      article: {
        primaryImage,
        primaryMedia,
        taxonomy,
      },
      vertical,
    } = this.props;

    const conditionalClasses = {
      'showblog-body__grid--container-no-main-image': !primaryImage && !primaryMedia,
    };

    const hasArticleSectionCounter = getFeatureConfigForBrand(
      ARTICLE_TAXONOMY_SECTION_NUMBERING,
      vertical,
    );

    if (Array.isArray(hasArticleSectionCounter)) {
      const termPaths = extractTermPaths(taxonomy);
      const hasMatchingClass = hasArticleSectionCounter.filter((tax) => termPaths.includes(tax));
      conditionalClasses['showblog-body__section-counter'] = hasMatchingClass.length > 0;
    }

    return conditionalClasses;
  }

  boxflexAdRenderCheck = (mpsAd) => {
    mpsAd.onRender((event) => {
      if (!event || event.empty) {
        return;
      }
      this.setState({
        boxflexRendered: true,
      });
    });
  }

  registerAd(el) {
    const ad = el && el.querySelector('.showblog-body__right-rail--ad');
    if (ad) {
      this.ads = [...this.ads, ad];
    }
  }

  interstitialAd() {
    const { article, vertical } = this.props;

    if (shouldShowAd(article) && [NEWS, TODAY].includes(vertical)) {
      return (
        <Ad
          slot="interstitial"
          refreshInterval={0}
        />
      );
    }

    return null;
  }

  renderSocialAndSponsored = ({
    bodyClasses, nativeAd, source,
  }) => (
    <div className="showblog-body__section">
      <div className={bodyClasses}>
        {nativeAd && source && source.copyright && (
          <p className="showblog-body__sponsored-by-text">{source?.copyright}</p>
        )}
      </div>
    </div>
  )

  renderRightRail = ({
    section, sectionIndex,
  }) => {
    const {
      path: currentPath,
    } = this.props;

    const { boxflexRendered, isScrollingUp, showTaboola } = this.state;
    return (
      <div
        className={`${block}--right-rail-container`}
        ref={(el) => this.registerAd(el)}
      >
        <BodyRightRail
          block={block}
          section={section}
          sectionIndex={sectionIndex}
          currentPath={currentPath}
          boxflexRendered={boxflexRendered}
          isScrollingUp={isScrollingUp}
          showTaboola={showTaboola}
          boxflexAdRenderCheck={this.boxflexAdRenderCheck}
        />
      </div>
    );
  }

  render() {
    const {
      article,
      gridBodyClasses,
      isChromeless,
      path,
      showBlogNav,
      vertical,
    } = this.props;

    if (!article) {
      return null;
    }

    const {
      hasStickyVideo, showTaboola,
    } = this.state;

    const {
      id,
      authors: articleAuthors = [],
      dateCreated,
      adsEnabled,
      nativeAd,
      source,
      datePublished,
    } = article;

    const authors = articleAuthors.filter(({ authorType }) => authorType === 'author');

    const articleBody = downsizeEmbeds(getNormalizedArticleBody(article));

    const sections = getSections({
      adsEnabled,
      article,
      articleBody,
      nativeAd,
      path,
      vertical,
      isChromeless,
    });

    const sectionsIndex = sections.map((section, index) => ({ index, ...section }));
    const sectionLast = sections.length - 1;

    const gridContainerClasses = classNames(
      'showblog-body__grid--container',
      this.getBodyContainerClasses(),
    );

    const dataTaboolaTarget = !isChromeless ? 'read-more' : '';

    return (
      <>
        <article className={block} data-taboola-target={dataTaboolaTarget}>
          <div className={gridContainerClasses}>
            {this.interstitialAd()}

            {this.renderSocialAndSponsored({ gridBodyClasses, nativeAd, source })}

            {sectionsIndex.map((section, i) => (
              <div
                key={section.index}
                className={classNames(
                  'showblog-body__section',
                  'layout-grid-container',
                  { 'showblog-body__last-section': i === sections.length - 1 },
                )}
              >
                <div
                  className={classNames(gridBodyClasses, 'layout-grid-item--with-gutter-s-only')}
                  data-testid="showblogBody"
                >
                  {i === 0 && (
                    <ShowBlogByline
                      authors={authors}
                      dateCreated={dateCreated}
                      datePublished={datePublished}
                      source={source}
                    />
                  )}
                  <div
                    className={
                      classNames(
                        'showblog-body__content',
                        {
                          'showblog-body__content--sticky-video': hasStickyVideo,
                        },
                      )
                    }
                  >
                    {/* Render section components */}
                    {section.items}
                  </div>
                  {i === sectionLast && (
                    <ExpandedBylineContributors
                      article={article}
                      additionalClasses="showBlogBylineContainer mt8"
                      contributorsAdditionalClasses="pv5"
                    />
                  )}
                  {showBlogNav && (
                    <ShowBlogNavigation
                      additionalClasses="mb9 mt8 showblog-navigation-bottom df flex-row flex-wrap"
                      currentArticleId={id}
                      previousArticle={showBlogNav.previous}
                      nextArticle={showBlogNav.next}
                      latestArticle={showBlogNav.latest}
                      showHeadlines
                      isBottomNavigation
                    />
                  )}
                </div>
                {this.renderRightRail({
                  section, sectionIndex: i,
                })}
              </div>
            ))}
            <BodyBottomRecommended
              block={block}
              currentPath={path}
              gridBodyClasses={gridBodyClasses}
              showTaboola={showTaboola}
            />
          </div>
        </article>

        <ArticleFoot vertical={vertical} currentPath={path} article={article} />

        {/* TODO? move to ArticleFoot */}
        <BodyTaboola
          article={article}
          showTaboola={showTaboola}
        />
      </>
    );
  }
}
